import React from 'react'
import { Chip, Typography } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { DEFAULT_COACH_IMAGE_URL } from 'src/config'
import { CoachDescription } from './CoachDescription'
import {
  CoachAvailabilityVideoCallSlot,
  coachAvailabilityVideoCallSlotEquals,
  CoachProfileWithVideoCallSlots,
} from '../useCoachAvailabilityDays'
import { CallBookingParticipantFragment } from '../../videoCallBookingHeaderQueries.generated'
import BookCallButton from './BookCallButton/BookCallButton'

const CoachProfile = ({
  coachWithSlots,
  selectedSlot,
  setSelectedSlot,
  onCloseDialog,
  participant,
}: {
  coachWithSlots: CoachProfileWithVideoCallSlots
  selectedSlot: CoachAvailabilityVideoCallSlot | null
  setSelectedSlot: (value: CoachAvailabilityVideoCallSlot | null) => void
  onCloseDialog: () => void
  participant: CallBookingParticipantFragment
}) => {
  const styles = useStyles()

  const { coachProfile } = coachWithSlots
  const videoCallSlots = coachWithSlots.videoCallSlot

  const coachName = `${coachProfile.firstname} ${coachProfile.lastname}`
  const coachPhotoUrl = coachProfile.photoUrl ?? DEFAULT_COACH_IMAGE_URL

  const isBookingEnabled =
    selectedSlot && videoCallSlots.some((slot) => coachAvailabilityVideoCallSlotEquals(slot, selectedSlot))

  return (
    <div className={styles.profileContainer}>
      <div className={styles.coachContainer}>
        <img src={coachPhotoUrl} className={styles.coachPhoto} alt="coach avatar" width={80} height={80} />
        <div>
          <Typography variant="h3" fontWeight="600">
            {coachName}
          </Typography>
          <Typography variant="body1" fontWeight="600">
            {coachProfile.title}
          </Typography>
          <CoachDescription description={coachProfile.description} />
        </div>
      </div>

      <div className={styles.bottomContainer}>
        <div className={styles.timeSlotButtonContainer}>
          {videoCallSlots.map((item: CoachAvailabilityVideoCallSlot) => {
            const isSelected = selectedSlot && coachAvailabilityVideoCallSlotEquals(selectedSlot, item)
            return (
              <Chip
                key={item.start.toISO()}
                clickable
                color={isSelected ? 'primary' : undefined}
                variant={isSelected ? undefined : 'outlined'}
                onClick={() => setSelectedSlot(isSelected ? null : item)}
                label={<span>{item.start.toFormat('t')}</span>}
              />
            )
          })}
        </div>
        <BookCallButton
          disabled={!isBookingEnabled}
          onCloseDialog={onCloseDialog}
          participant={participant}
          selectedSlot={selectedSlot}
        />
      </div>
    </div>
  )
}

const useStyles = makeStyles((theme) => ({
  profileContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.lightestGrey}`,
    borderRadius: theme.spacing(4),
    marginBottom: theme.spacing(4),
  },
  coachPhoto: {
    marginRight: theme.spacing(2),
  },
  coachContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  bottomContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: theme.spacing(4),
  },
  timeSlotButtonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
  },
}))

export default CoachProfile
